<template>
  <div class="h-screen pt-safe pb-safe sm:bg-corner-gradient">
    <div v-if="!isLoading" class="px-6 sm:px-12 w-full h-full max-w-[42rem] mx-auto pt-8 flex flex-col">
      <div class="text-n-0 text-3xl font-heading font-bold mb-8 text-center shrink-0">
        {{ t('locations.title') }}
      </div>
      <div class="mb-4 shrink-0">
        <l-search-bar
          v-model="searchTerm"
          size="large"
          :placeholder="t('locations.search-placeholder')"
        />
      </div>
      <transition-group
        tag="div"
        name="gridtransition"
        class="w-full grid grid-cols-1 md:grid-cols-2 gap-4 pb-4 overflow-auto min-h-0"
      >
        <div
          v-for="location of filteredLocations"
          :key="location.id"
          class="text-n-0 bg-n-700 rounded px-4 py-6 cursor-pointer"
          @click="selectLocationAndContinue(location)"
        >
          <div class="flex flex-col">
            <div class="flex flex-row mb-2 items-center">
              <div class="mr-3 w-4 h-4 shrink-0 grow-0">
                <l-icon size="small" name="store" />
              </div>
              <div class="grow">
                {{ location.name }}
              </div>
              <div class="mr-3 w-4 h-4 shrink-0 grow-0">
                <l-icon size="small" name="bracket-right" />
              </div>
            </div>
            <div class="text-sm text-n-200">{{ location.address }}</div>
          </div>
        </div>
      </transition-group>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Capacitor } from '@capacitor/core'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import { LIcon, LSearchBar } from '@last/core-ui/paprika'

import { authApi } from '@/api/client'
import { addDevice } from '@/api/device'
import { useAuthStore } from '@/store/auth'
import sync from '@/sync/service'
import type { Location } from '@/types'
import { getDeviceInfo } from '@/utils/device'

const authStore = useAuthStore()
const router = useRouter()
const { t } = useI18n()

const searchTerm = ref<string>('')
const locations = ref<Location[]>([])
const isLoading = ref(true)

onMounted(() => {
  allLocations()
})

const filteredLocations = computed(() => {
  if (searchTerm.value) {
    return locations.value.filter(location =>
      location.name.toLowerCase().includes(searchTerm.value.toLowerCase())
    )
  }
  return locations.value
})

async function allLocations() {
  const allLocations = await authApi.getLocations()
  if (allLocations.length === 1) {
    selectLocationAndContinue(allLocations[0])
  } else {
    locations.value = allLocations
    isLoading.value = false
  }
}

async function selectLocationAndContinue(location: Location): Promise<void> {
  const platform = Capacitor.getPlatform()
  if (platform !== 'web') {
    const deviceInfo = await getDeviceInfo()
    try {
      authStore.locationId = location.id
      const newDevice = await addDevice(deviceInfo)
      authStore.setAccessToken(newDevice.accessToken)
    } catch {
      authStore.locationId = undefined
      await router.push({
        name: 'overTheLimit'
      })
      return
    }
  }
  authStore.selectLocation(location)
  sync.resync()
  router.push({ name: 'employees' })
}
</script>

<style>
.gridtransition-enter-active {
  transition:
    transform 100ms ease,
    opacity 100ms ease-out;
}

.gridtransition-leave-active {
  transition:
    transform 100ms ease,
    opacity 100ms ease-in;
}

.gridtransition-enter-from,
.gridtransition-leave-to {
  opacity: 0;
  transform: scale(0.9);
}
</style>
